import React, { useCallback, useContext } from 'react';
import { InstagramGridView, InstagramGridViewProps } from './InstagramGrid.view';
import {
  PostOrderUpdateDirection,
  PostQueuePosition,
  SocialProfileFieldsFragment,
  UpdatePostOrderMutationFn
} from '../../../models/graphql';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { FormElementAction } from '../../../models/basic-objects';
import { AppContext } from '../../../providers/AppProvider';
import { ApolloError, useApolloClient } from '@apollo/client';
import { InstagramGridContext } from '../../../providers/InstagramGridProvider';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { InstagramGridPostType } from '../../../models/post';

export type InstagramGridControllerProps = {
  instagramProfiles: SocialProfileFieldsFragment[];
  getPostScheduleListItems: () => void;
  getInstagramSocialProfiles: () => void;
  refetchPosts: () => void;
  updatePostOrder: UpdatePostOrderMutationFn;
  movePostPosition: (postId: string, position: PostQueuePosition) => void;
  trashSinglePost: (postId: string) => void;
  igGridPosts: InstagramGridPostType[];
  igApiError?: string;
};

export const InstagramGridController = (props: InstagramGridControllerProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { selectedWorkspaceId } = useContext(AppContext);
  const { setSelectedProfileId } = useContext(InstagramGridContext);
  const { getPostScheduleListItems, updatePostOrder } = props;

  const useFormInstance = useForm({
    ...FormOptions
  });

  const profileSelectAction: FormElementAction<string> = (value: string, complete) => {
    cache.modify({
      id: cache.identify({ id: selectedWorkspaceId, __typename: 'Workspace' }),
      fields: {
        queue() {
          // This removes every post under the workspace.
          // We can set this to `undefined` to re-trigger active workspace queries if needed.
          // We can also set it to undefined and set `broadcast: false` below if we want to reFetch this single query
          return [];
        }
      }
    });
    cache.gc();

    setSelectedProfileId(value);
    complete(true);
  };

  const updatePostLocation = useCallback(
    async (postId: string, refPostId: string, direction: PostOrderUpdateDirection) => {
      if (postId === refPostId) {
        return;
      }

      await updatePostOrder({
        variables: {
          postId: postId,
          relativePostId: refPostId,
          direction: direction
        }
      })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Could not move the post',
            detail: remapApolloErrors(error)
          });
        })
        .finally(() => getPostScheduleListItems());
    },
    [getPostScheduleListItems, updatePostOrder]
  );

  const viewProps: InstagramGridViewProps = {
    ...props,
    useFormInstance,
    profileSelectAction,
    updatePostLocation,
    getPostScheduleListItems
  };

  return <InstagramGridView {...viewProps} />;
};
